// Comprehensive services data for SEG Career Support
// Individual coaching services (redirect to CareerSupport.io later)
// Workshops (Stripe checkout on SEG)

import { 
  FileText, 
  Linkedin, 
  UserCheck, 
  Target, 
  Briefcase, 
  MessageSquare,
  GraduationCap,
  Users,
  Clock,
  CheckCircle,
  Award,
  TrendingUp,
  Zap,
  Shield,
  Star,
  FolderOpen,
  Shirt,
  Search,
  Eye,
  DollarSign,
  FileEdit,
  PenTool,
  Share2,
  Edit3
} from 'lucide-react';

// Career Strategy Session - Single 60-min comprehensive session
export const careerStrategySession = {
  id: "career-strategy-session",
  slug: "career-strategy-session",
  title: "Career Strategy Session",
  tagline: "Comprehensive guidance in one powerful session",
  shortDescription: "A 60-minute in-depth session where we build your complete career strategy, covering any aspect of your professional journey.",
  heroDescription: "Stop spinning your wheels on your career. In one comprehensive 60-minute session, we'll dive deep into your career challenges and build a personalized strategy that covers everything from resume positioning to job search tactics to salary negotiation. This isn't a quick chat—it's a strategic planning session that will give you clarity and direction.",
  icon: Target,
  image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
  pricing: {
    perSession: 150,
    duration: "60 minutes"
  },
  processIntro: "Start with a complimentary 20-minute consultation to assess your goals, then dive into your 60-minute strategy session.",
  whatItSolves: [
    "You're feeling stuck and don't know your next career move",
    "You need expert guidance but don't want an ongoing coaching commitment",
    "You have multiple career concerns you want to address in one session",
    "You want a customized action plan, not generic advice",
    "You're preparing for a major career decision or transition"
  ],
  steps: [
    {
      number: 1,
      title: "Free 20-Minute Career Strategy Session",
      description: "Schedule a complimentary 20-minute call where we assess your goals, challenges, and determine the best focus areas for your strategy session.",
      timeframe: "20 minutes",
      isFree: true
    },
    {
      number: 2,
      title: "Pre-Session Prep",
      description: "Complete a brief questionnaire about your background, current situation, and goals. This ensures we maximize every minute of your session.",
      timeframe: "1-2 days"
    },
    {
      number: 3,
      title: "Strategy Session",
      description: "Your 60-minute deep-dive session where we tackle your career challenges head-on. We'll cover whatever you need: resume strategy, job search, networking, interview prep, salary negotiation, or career planning.",
      timeframe: "60 minutes"
    },
    {
      number: 4,
      title: "Action Plan Delivery",
      description: "Receive a written summary of your personalized strategy with clear action steps, resources, and recommendations to implement immediately.",
      timeframe: "Within 24 hours"
    }
  ],
  sessionCanCover: [
    { title: "Resume & LinkedIn Strategy", description: "Positioning your experience for maximum impact" },
    { title: "Job Search Tactics", description: "Developing a targeted, efficient search strategy" },
    { title: "Interview Preparation", description: "Mastering behavioral questions and presentations" },
    { title: "Salary Negotiation", description: "Strategies to maximize your compensation" },
    { title: "Career Transition", description: "Planning your move to a new field or role" },
    { title: "Networking Strategy", description: "Building connections that advance your career" }
  ],
  benefits: [
    {
      title: "Holistic Approach",
      description: "Address multiple career concerns in one focused session—no need to book separate appointments.",
      icon: Target
    },
    {
      title: "Expert Guidance",
      description: "Work directly with a certified career strategist who has helped thousands of professionals.",
      icon: Award
    },
    {
      title: "Immediate Action",
      description: "Leave with a clear action plan you can start implementing right away.",
      icon: Zap
    }
  ],
  stats: {
    clientSatisfaction: "98%",
    clarityAchieved: "95%",
    avgTimeSaved: "3+ months"
  },
  serviceType: "strategy-session"
};

// Career Coaching Packages - Long-term partnerships
export const careerCoachingPackages = {
  id: "career-coaching-packages",
  slug: "career-coaching-packages",
  title: "Career Coaching Packages",
  tagline: "Ongoing Coaching & Career Partnerships",
  heroDescription: "Designed for professionals who want consistent guidance, measurable progress, and accountability. Choose the partnership that matches your career ambitions.",
  icon: TrendingUp,
  image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800",
  processIntro: "Start with a free 20-minute career strategy session, select your coaching path, then complete your initial strategy session.",
  packages: [
    {
      id: "3-month",
      title: "3-Month Career Acceleration",
      duration: "3 months",
      price: "$997",
      idealFor: "Job seekers and career pivots",
      includes: [
        "Biweekly 60-minute coaching sessions",
        "Career roadmap creation",
        "Resume positioning strategy",
        "LinkedIn optimization guidance",
        "Job search system buildout",
        "Interview preparation",
        "Accountability tracking",
        "Email support between sessions",
        "Access to CareerSupport.io Premium"
      ],
      outcomes: [
        "A refined professional brand",
        "A structured job search system",
        "Interview readiness",
        "Measurable progress"
      ],
      ctaText: "Book 3-Month Coaching Program",
      highlight: false
    },
    {
      id: "6-month",
      title: "6-Month Career Growth Partnership",
      duration: "6 months",
      price: "$1,997",
      idealFor: "Professionals seeking advancement and income growth",
      includes: [
        "Everything in 3-month package plus:",
        "Advanced networking strategy",
        "Salary negotiation preparation",
        "Personal brand refinement",
        "Skills gap analysis",
        "Leadership positioning strategy",
        "Career advancement mapping",
        "Priority scheduling",
        "Extended support access"
      ],
      outcomes: [
        "Career clarity and market positioning",
        "Promotion or income growth strategy",
        "Expanded professional network",
        "Increased confidence and leadership presence"
      ],
      ctaText: "Book 6-Month Coaching Program",
      highlight: true
    },
    {
      id: "executive",
      title: "12-Month Executive Development",
      duration: "12 months",
      price: "$3,997",
      idealFor: "High-performing professionals and leaders",
      includes: [
        "Long-term career architecture planning",
        "Executive presence development",
        "Compensation growth strategy",
        "Strategic positioning within industry",
        "Quarterly deep-dive planning sessions",
        "Ongoing advisory access",
        "Full CareerSupport.io platform access",
        "Phone and priority email support"
      ],
      outcomes: [
        "Long-term growth roadmap",
        "Strategic positioning",
        "Executive-level confidence",
        "Sustainable career momentum"
      ],
      ctaText: "Book 12-Month Coaching Program",
      highlight: false
    }
  ],
  processSteps: [
    {
      number: 1,
      title: "Schedule a Free 20-Minute Career Strategy Session",
      description: "We assess your goals and determine fit.",
      timeframe: "20 minutes"
    },
    {
      number: 2,
      title: "Select Your Coaching Path",
      description: "Short-term acceleration or long-term growth partnership.",
      timeframe: null
    },
    {
      number: 3,
      title: "Complete Initial Strategy Session",
      description: "We assist you with building your customized plan.",
      timeframe: "60 minutes"
    }
  ],
  faqs: [
    {
      question: "Do I need to use CareerSupport.io?",
      answer: "While not required, CareerSupport.io provides valuable tools and resources that complement your coaching experience. Premium access is included with all packages."
    },
    {
      question: "Can I purchase additional sessions?",
      answer: "Absolutely! If you need additional sessions beyond your package, you can add individual sessions at any time. Many clients choose to add sessions around critical career moments."
    },
    {
      question: "Are payment plans available?",
      answer: "Yes, we offer flexible payment plans for all coaching packages. During your consultation, we'll discuss payment options that work for your budget."
    },
    {
      question: "Is this confidential?",
      answer: "100% confidential. All coaching conversations and materials are private. We never share your information with anyone, including current or potential employers."
    },
    {
      question: "Can my employer sponsor coaching?",
      answer: "Yes! Many of our clients have their coaching sponsored by their employer as professional development. We can provide documentation for reimbursement requests."
    },
    {
      question: "What industries do you serve?",
      answer: "We work with professionals across all industries including technology, healthcare, finance, education, non-profit, government, and more. Our strategies are tailored to your specific field."
    }
  ],
  serviceType: "coaching-packages"
};

// Individual Coaching Services - Will redirect to CareerSupport.io
export const coachingServices = [
  {
    id: "resume-revision",
    slug: "resume-revision",
    title: "Resume Revision",
    tagline: "The resume that opens doors",
    shortDescription: "We craft resumes that get noticed by hiring managers and applicant tracking systems.",
    heroDescription: "Your resume is your first impression. We've helped thousands of professionals transform their resumes into powerful career tools that land interviews. Our expert team of hiring managers and recruiters know exactly what employers are looking for.",
    icon: FileText,
    image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800",
    pricing: {
      oneTime: 250,
      installment: 65,
      installmentCount: 4,
      installmentFrequency: "bi-weekly"
    },
    whatItSolves: [
      "Your resume isn't getting responses from employers",
      "You're unsure how to highlight your achievements effectively",
      "Your resume doesn't pass applicant tracking systems (ATS)",
      "You're transitioning careers and need to reframe your experience",
      "You want to stand out in a competitive job market"
    ],
    steps: [
      {
        number: 1,
        title: "Submit Your Resume",
        description: "Send us your current resume along with your career goals, target roles, and industry preferences. We'll review everything to understand your unique situation.",
        timeframe: null
      },
      {
        number: 2,
        title: "Strategy Call",
        description: "We'll schedule a consultation to discuss your background, accomplishments, and the roles you're targeting. This helps us tailor your resume perfectly.",
        timeframe: "1-2 days"
      },
      {
        number: 3,
        title: "Professional Rewrite",
        description: "Our team of hiring managers and recruiters will completely revise your resume, optimizing it for both ATS systems and human readers.",
        timeframe: "2-3 days"
      },
      {
        number: 4,
        title: "Review Session",
        description: "We'll walk through your new resume together, explaining our strategic choices and making any adjustments based on your feedback.",
        timeframe: "2 days"
      },
      {
        number: 5,
        title: "Final Delivery",
        description: "Receive your polished, professional resume in multiple formats (Word, PDF) ready to submit to employers.",
        timeframe: "1-2 days"
      }
    ],
    benefits: [
      {
        title: "Get Noticed",
        description: "Your resume will be crafted by professionals who know what hiring managers look for.",
        icon: Target
      },
      {
        title: "Land Interviews",
        description: "Most of our clients see interview requests within 4-8 weeks of using their new resume.",
        icon: CheckCircle
      },
      {
        title: "Career Advancement",
        description: "A strong resume is the foundation for salary negotiations and career growth.",
        icon: TrendingUp
      }
    ],
    stats: {
      clientsHelped: "5,000+",
      interviewRate: "85%",
      avgTimeToInterview: "4-8 weeks"
    },
    serviceType: "coaching",
    isPopular: true
  },
  {
    id: "linkedin-optimization",
    slug: "linkedin-optimization",
    title: "LinkedIn Optimization",
    tagline: "Get discovered by recruiters",
    shortDescription: "Transform your LinkedIn profile into a powerful networking and job search tool.",
    heroDescription: "87% of recruiters use LinkedIn to find candidates. Your profile is your digital first impression, working for you 24/7. We'll optimize every section to attract opportunities and establish your professional brand.",
    icon: Linkedin,
    image: "https://images.unsplash.com/photo-1611944212129-29977ae1398c?w=800",
    pricing: {
      oneTime: 250,
      installment: 65,
      installmentCount: 4,
      installmentFrequency: "bi-weekly"
    },
    whatItSolves: [
      "Recruiters aren't finding your profile",
      "You're not sure how to present yourself professionally online",
      "Your profile doesn't reflect your true expertise and value",
      "You want to build a stronger professional network",
      "You need to establish thought leadership in your industry"
    ],
    steps: [
      {
        number: 1,
        title: "Profile Audit",
        description: "Share your LinkedIn profile and career goals. We'll analyze what's working and identify opportunities for improvement.",
        timeframe: null
      },
      {
        number: 2,
        title: "Strategy Session",
        description: "We'll discuss your professional brand, target audience, and how to position yourself as an industry expert.",
        timeframe: "1-2 days"
      },
      {
        number: 3,
        title: "Profile Transformation",
        description: "Our team will rewrite your headline, about section, experience, and optimize keywords for search visibility.",
        timeframe: "2-3 days"
      },
      {
        number: 4,
        title: "Profile Review",
        description: "Walk through your optimized profile together, with tips on engagement strategies and content creation.",
        timeframe: "2 days"
      },
      {
        number: 5,
        title: "Launch Your Brand",
        description: "Implement your new profile and receive a guide for ongoing LinkedIn success and networking strategies.",
        timeframe: "1-2 days"
      }
    ],
    benefits: [
      {
        title: "Increased Visibility",
        description: "Appear in more recruiter searches with strategic keyword optimization.",
        icon: Target
      },
      {
        title: "Professional Brand",
        description: "Present a cohesive, compelling professional image that attracts opportunities.",
        icon: Award
      },
      {
        title: "Network Growth",
        description: "Build meaningful connections with industry professionals and decision makers.",
        icon: Users
      }
    ],
    stats: {
      profileViewIncrease: "300%",
      connectionRequestIncrease: "150%",
      recruiterInquiries: "5x more"
    },
    serviceType: "coaching",
    isPopular: false
  },
  {
    id: "interview-prep",
    slug: "interview-prep",
    title: "Interview Preparation",
    tagline: "Ace your next interview",
    shortDescription: "Train with industry professionals to master behavioral interviews and land offers.",
    heroDescription: "Landing an interview is only half the battle. Our interview coaching prepares you to confidently handle any question, from behavioral scenarios to salary negotiations. Train with professionals who've conducted thousands of interviews.",
    icon: UserCheck,
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800",
    pricing: {
      oneTime: 250,
      installment: 65,
      installmentCount: 4,
      installmentFrequency: "bi-weekly"
    },
    whatItSolves: [
      "You feel nervous or unprepared for interviews",
      "You struggle to articulate your accomplishments clearly",
      "You've been getting interviews but not offers",
      "You want to negotiate a better salary package",
      "You're preparing for a high-stakes interview opportunity"
    ],
    steps: [
      {
        number: 1,
        title: "Assessment",
        description: "Complete our interview readiness questionnaire. Tell us about the role and company you're interviewing with.",
        timeframe: null
      },
      {
        number: 2,
        title: "Strategy Planning",
        description: "We'll develop a customized prep plan based on your target role, industry, and the specific company culture.",
        timeframe: "1-2 days"
      },
      {
        number: 3,
        title: "Mock Interview #1",
        description: "Experience a realistic 45-minute mock interview with detailed feedback on your responses, body language, and presentation.",
        timeframe: "2 days"
      },
      {
        number: 4,
        title: "Coaching & Refinement",
        description: "Receive personalized coaching to strengthen weak areas and develop powerful stories using the STAR method.",
        timeframe: "2-3 days"
      },
      {
        number: 5,
        title: "Mock Interview #2",
        description: "Put your training into practice with a second mock interview and final feedback session.",
        timeframe: "2 days"
      },
      {
        number: 6,
        title: "Interview Ready",
        description: "Go into your interview with confidence, armed with strategies and responses tailored to your opportunity.",
        timeframe: "1 day"
      }
    ],
    benefits: [
      {
        title: "Real Practice",
        description: "Two full mock interviews with professionals who've been on hiring panels.",
        icon: UserCheck
      },
      {
        title: "Personalized Feedback",
        description: "Detailed coaching on your specific strengths and areas for improvement.",
        icon: MessageSquare
      },
      {
        title: "Confidence Boost",
        description: "Walk into any interview knowing you're prepared for whatever they ask.",
        icon: Shield
      }
    ],
    stats: {
      offerRate: "78%",
      clientSatisfaction: "95%",
      avgSalaryIncrease: "15%"
    },
    serviceType: "coaching",
    isPopular: false
  },
  {
    id: "job-search-strategy",
    slug: "job-search-strategy",
    title: "Job Search Strategy",
    tagline: "Navigate your search with a plan",
    shortDescription: "Get a customized roadmap to find and land your ideal role faster.",
    heroDescription: "Don't just apply to jobs randomly. Work with a career strategist to develop a targeted approach that gets results. We'll help you identify the right opportunities, optimize your applications, and accelerate your job search.",
    icon: Target,
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
    pricing: {
      oneTime: 250,
      installment: 65,
      installmentCount: 4,
      installmentFrequency: "bi-weekly"
    },
    whatItSolves: [
      "You're applying to jobs but not hearing back",
      "You're unsure which roles or industries are right for you",
      "Your job search feels unfocused and overwhelming",
      "You want to explore new career directions strategically",
      "You need guidance on networking and hidden job markets"
    ],
    steps: [
      {
        number: 1,
        title: "Career Discovery",
        description: "Complete our comprehensive assessment covering your skills, interests, values, and career aspirations.",
        timeframe: null
      },
      {
        number: 2,
        title: "Initial Consultation",
        description: "Meet with your career strategist to discuss your background, goals, and what success looks like for you.",
        timeframe: "1-2 days"
      },
      {
        number: 3,
        title: "Strategy Development",
        description: "We create your personalized job search playbook with target companies, roles, and networking strategies.",
        timeframe: "2-3 days"
      },
      {
        number: 4,
        title: "Deep Dive Session",
        description: "Walk through your strategy together, with actionable steps and timeline for your search.",
        timeframe: "2 days"
      },
      {
        number: 5,
        title: "Implementation Support",
        description: "Receive your complete strategy deck and ongoing support as you execute your job search plan.",
        timeframe: "2 days"
      }
    ],
    benefits: [
      {
        title: "Clear Direction",
        description: "Know exactly what roles to target and how to position yourself for them.",
        icon: Target
      },
      {
        title: "Efficient Search",
        description: "Stop wasting time on unsuitable opportunities and focus where it matters.",
        icon: Zap
      },
      {
        title: "Hidden Opportunities",
        description: "Learn how to tap into the hidden job market through strategic networking.",
        icon: Users
      }
    ],
    stats: {
      timeToEmployment: "60% faster",
      targetedApplications: "300% more effective",
      clientSuccess: "90%"
    },
    serviceType: "coaching",
    isPopular: false
  },
  {
    id: "career-consulting",
    slug: "career-consulting",
    title: "Career Consulting",
    tagline: "One-on-one expert guidance",
    shortDescription: "Get personalized career advice from experienced industry professionals.",
    heroDescription: "Whether you're planning a career change, seeking advancement, or navigating workplace challenges, our career consultants provide the guidance you need. Get expert perspective on your unique situation.",
    icon: Briefcase,
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800",
    pricing: {
      oneTime: 250,
      installment: 65,
      installmentCount: 4,
      installmentFrequency: "bi-weekly"
    },
    whatItSolves: [
      "You're at a career crossroads and need expert guidance",
      "You want to accelerate your path to leadership",
      "You're dealing with workplace challenges or transitions",
      "You need help defining your career path and goals",
      "You want accountability and support in your career journey"
    ],
    steps: [
      {
        number: 1,
        title: "Initial Assessment",
        description: "Complete our career assessment and share your current situation, challenges, and aspirations.",
        timeframe: null
      },
      {
        number: 2,
        title: "Discovery Session",
        description: "In-depth consultation to understand your background, goals, and what's holding you back.",
        timeframe: "1-2 days"
      },
      {
        number: 3,
        title: "Analysis & Planning",
        description: "Your consultant develops personalized recommendations based on your unique situation.",
        timeframe: "2-3 days"
      },
      {
        number: 4,
        title: "Strategy Session",
        description: "Review your career roadmap with specific actions, timelines, and success metrics.",
        timeframe: "2 days"
      },
      {
        number: 5,
        title: "Implementation",
        description: "Receive your career guide and begin executing your plan with ongoing support.",
        timeframe: "2 days"
      }
    ],
    benefits: [
      {
        title: "Expert Perspective",
        description: "Get insights from professionals who've navigated similar career paths.",
        icon: Award
      },
      {
        title: "Customized Advice",
        description: "No generic tips - every recommendation is tailored to your situation.",
        icon: Target
      },
      {
        title: "Accountability",
        description: "Have someone in your corner keeping you focused and on track.",
        icon: CheckCircle
      }
    ],
    stats: {
      careerClarity: "95%",
      goalAchievement: "85%",
      clientSatisfaction: "98%"
    },
    serviceType: "coaching",
    isPopular: false
  },
  {
    id: "networking-strategy",
    slug: "networking-strategy",
    title: "Networking Strategy",
    tagline: "Build connections that advance your career",
    shortDescription: "Learn strategic networking techniques to build meaningful professional relationships.",
    heroDescription: "Your network is your net worth. 85% of jobs are filled through networking, yet most professionals don't know how to network effectively. We'll help you build authentic relationships that open doors to opportunities you never knew existed.",
    icon: Users,
    image: "https://images.unsplash.com/photo-1552581234-26160f608093?w=800",
    pricing: {
      oneTime: 250,
      installment: 65,
      installmentCount: 4,
      installmentFrequency: "bi-weekly"
    },
    whatItSolves: [
      "You don't know how to start networking conversations",
      "Your networking efforts aren't producing results",
      "You feel uncomfortable at networking events",
      "You want to build relationships with industry leaders",
      "You need to expand your professional circle strategically"
    ],
    steps: [
      {
        number: 1,
        title: "Network Assessment",
        description: "We'll map your current network and identify gaps in your professional connections based on your career goals.",
        timeframe: null
      },
      {
        number: 2,
        title: "Strategy Session",
        description: "Develop a personalized networking plan targeting the right people, industries, and opportunities for your career.",
        timeframe: "1-2 days"
      },
      {
        number: 3,
        title: "Outreach Training",
        description: "Learn proven techniques for reaching out to new contacts, including email templates, LinkedIn messaging, and conversation starters.",
        timeframe: "2-3 days"
      },
      {
        number: 4,
        title: "Relationship Building",
        description: "Master the art of nurturing professional relationships through follow-ups, value exchange, and authentic engagement.",
        timeframe: "2 days"
      },
      {
        number: 5,
        title: "Action Plan",
        description: "Receive a customized 90-day networking action plan with specific targets, scripts, and accountability milestones.",
        timeframe: "1-2 days"
      }
    ],
    benefits: [
      {
        title: "Expand Your Reach",
        description: "Connect with industry professionals, mentors, and decision-makers who can advance your career.",
        icon: Users
      },
      {
        title: "Hidden Opportunities",
        description: "Access the 85% of jobs that are never publicly posted through your network.",
        icon: Search
      },
      {
        title: "Lasting Relationships",
        description: "Build authentic connections that provide value throughout your entire career.",
        icon: Award
      }
    ],
    stats: {
      networkGrowth: "200%",
      opportunitiesGenerated: "10+ monthly",
      connectionRate: "75%"
    },
    serviceType: "coaching",
    isPopular: false
  },
  {
    id: "assessment-discovery",
    slug: "assessment-discovery",
    title: "Assessment & Discovery",
    tagline: "Discover your true career direction",
    shortDescription: "Comprehensive career assessments to uncover your strengths, interests, and ideal career path.",
    heroDescription: "Before you can reach your destination, you need to know where you're going. Our comprehensive assessment and discovery process helps you understand your unique strengths, values, interests, and personality to identify the career path that's truly right for you.",
    icon: Search,
    image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800",
    pricing: {
      oneTime: 250,
      installment: 65,
      installmentCount: 4,
      installmentFrequency: "bi-weekly"
    },
    whatItSolves: [
      "You're unsure what career path is right for you",
      "You feel stuck and don't know your next step",
      "You want to understand your unique strengths and talents",
      "You're considering a career change but don't know where to start",
      "You need clarity on your professional values and priorities"
    ],
    steps: [
      {
        number: 1,
        title: "Intake Questionnaire",
        description: "Complete our comprehensive background questionnaire covering your education, experience, interests, and career aspirations.",
        timeframe: null
      },
      {
        number: 2,
        title: "Professional Assessments",
        description: "Take industry-standard assessments including personality type, strengths finder, values inventory, and interest profiler.",
        timeframe: "1-2 days"
      },
      {
        number: 3,
        title: "Deep-Dive Analysis",
        description: "Our career strategist analyzes your assessment results to identify patterns, strengths, and optimal career directions.",
        timeframe: "2-3 days"
      },
      {
        number: 4,
        title: "Discovery Session",
        description: "In-depth consultation to review your results, explore career options, and align opportunities with your authentic self.",
        timeframe: "2 days"
      },
      {
        number: 5,
        title: "Career Roadmap",
        description: "Receive a personalized career roadmap with recommended paths, action steps, and resources for your journey.",
        timeframe: "1-2 days"
      }
    ],
    benefits: [
      {
        title: "Self-Awareness",
        description: "Gain deep understanding of your personality, strengths, and what truly motivates you.",
        icon: Eye
      },
      {
        title: "Clarity & Direction",
        description: "End confusion and uncertainty with a clear vision of your ideal career path.",
        icon: Target
      },
      {
        title: "Confidence",
        description: "Make career decisions with confidence knowing they align with who you really are.",
        icon: Shield
      }
    ],
    stats: {
      clarityAchieved: "98%",
      clientSatisfaction: "96%",
      careerShifts: "70%"
    },
    serviceType: "coaching",
    isPopular: false
  }
];

// Workshop pricing tiers based on duration and group size
export const workshopPricingMatrix = {
  durations: [
    { id: "overview", label: "Overview Session", minutes: 45, multiplier: 1.0 },
    { id: "standard", label: "Standard Workshop", minutes: 60, multiplier: 1.5 },
    { id: "extended", label: "Extended Workshop", minutes: 90, multiplier: 2.0 },
    { id: "half-day", label: "Half-Day Training", minutes: 240, multiplier: 4.0 },
    { id: "full-day", label: "Full-Day Training", minutes: 480, multiplier: 7.5 }
  ],
  groupSizes: [
    { id: "small", label: "Small Group (1-5)", min: 1, max: 5, basePrice: 150 },
    { id: "medium", label: "Medium Group (6-20)", min: 6, max: 20, basePrice: 300 },
    { id: "large", label: "Large Group (21-50)", min: 21, max: 50, basePrice: 500 },
    { id: "enterprise", label: "Enterprise (51-100)", min: 51, max: 100, basePrice: 800 }
  ]
};

// Unified Workshop Topics - All topics with consistent structure
// Topics with hasDetailPage=true have detailed resource pages at /workshop/{slug}
export const workshopTopics = [
  // === Original Workshop Topics (General Categories) - Now with detail pages ===
  {
    id: "career-development-overview",
    slug: "career-development-overview",
    title: "Career Development Overview",
    category: "general",
    shortDescription: "A comprehensive introduction to career development strategies and professional growth.",
    fullDescription: "This foundational workshop provides participants with essential career development concepts, goal-setting frameworks, and actionable strategies for professional advancement. Perfect for teams looking to invest in their employees' growth.",
    targetAudience: ["HR Teams", "New Employees", "Career Changers", "Students"],
    outcomes: [
      "Understand key career development principles",
      "Create personal career goals and action plans",
      "Learn networking and personal branding basics",
      "Identify growth opportunities within organizations"
    ],
    icon: TrendingUp,
    hasDetailPage: true
  },
  {
    id: "workforce-development-intensive",
    slug: "workforce-development-intensive",
    title: "Workforce Development Intensive",
    category: "corporate",
    shortDescription: "Comprehensive workforce readiness training for organizations.",
    fullDescription: "A complete workforce development program covering essential professional skills, from communication to career planning. Ideal for workforce development agencies and educational institutions.",
    targetAudience: ["Workforce Agencies", "Community Colleges", "Non-Profits", "Government Programs"],
    outcomes: [
      "Develop job-ready professional skills",
      "Create career pathways and goals",
      "Build essential soft skills",
      "Prepare for workplace success"
    ],
    icon: Users,
    hasDetailPage: true
  },
  {
    id: "leadership-career-growth",
    slug: "leadership-career-growth",
    title: "Leadership & Career Growth",
    category: "leadership",
    shortDescription: "Strategies for advancing into leadership roles and maximizing career potential.",
    fullDescription: "This workshop focuses on developing leadership capabilities, strategic thinking, and the skills needed to advance into management and executive positions.",
    targetAudience: ["Emerging Leaders", "Mid-Level Managers", "High-Potential Employees", "Entrepreneurs"],
    outcomes: [
      "Identify leadership development opportunities",
      "Build executive presence and communication",
      "Create strategic career advancement plans",
      "Develop mentoring and sponsorship relationships"
    ],
    icon: Award,
    hasDetailPage: true
  },
  {
    id: "salary-negotiation-mastery",
    slug: "salary-negotiation-mastery",
    title: "Salary Negotiation Mastery",
    category: "career-advancement",
    shortDescription: "Learn proven techniques to negotiate better compensation packages.",
    fullDescription: "Empower participants with the skills and confidence to negotiate salaries, benefits, and promotions effectively. Includes role-play exercises and real-world scenarios.",
    targetAudience: ["Job Seekers", "Employees Seeking Raises", "HR Professionals", "Managers"],
    outcomes: [
      "Research and determine your market value",
      "Prepare compelling negotiation arguments",
      "Handle counter-offers professionally",
      "Negotiate beyond just salary (benefits, flexibility)"
    ],
    icon: TrendingUp,
    hasDetailPage: true
  },
  // === Presentation Topics (With Detailed Resource Pages) ===
  {
    id: "job-search-cover-letters",
    slug: "job-search-cover-letters",
    title: "Job Search & Cover Letters",
    category: "job-search",
    shortDescription: "Designed to help students understand the academic job search and prepare a cover letter in an academic job campaign.",
    fullDescription: "A strategic job search combined with a compelling cover letter can be the difference between landing your dream job and getting lost in a sea of applicants.",
    targetAudience: ["Job Seekers", "Students", "Career Changers"],
    outcomes: [
      "Research companies before applying",
      "Customize every cover letter for impact",
      "Quantify achievements effectively",
      "Include strong calls to action"
    ],
    icon: FileText,
    hasDetailPage: true
  },
  {
    id: "building-linkedin-profile",
    slug: "building-linkedin-profile",
    title: "Building a LinkedIn Profile",
    category: "networking",
    shortDescription: "Learn why LinkedIn is an essential personal branding tool, and explore how to utilize it for professional networking, the job search, and more.",
    fullDescription: "LinkedIn has become the world's largest professional networking platform. A well-optimized LinkedIn profile is essential for personal branding, networking, and job searching.",
    targetAudience: ["Professionals", "Job Seekers", "Students", "Business Owners"],
    outcomes: [
      "Create a professional profile photo and headline",
      "Write an engaging About section",
      "Optimize for recruiter searches",
      "Build meaningful professional connections"
    ],
    icon: Linkedin,
    hasDetailPage: true
  },
  {
    id: "business-etiquette",
    slug: "business-etiquette",
    title: "Business Etiquette",
    category: "professional-skills",
    shortDescription: "Introduces the basics of business etiquette and the importance of proper etiquette as it relates to interviewing and business situations.",
    fullDescription: "Business etiquette encompasses the unwritten rules that govern professional interactions. Mastering these social norms can significantly impact your career success.",
    targetAudience: ["New Graduates", "International Professionals", "Customer-Facing Teams", "Interns"],
    outcomes: [
      "Master the art of professional introductions",
      "Navigate meeting and dining etiquette",
      "Demonstrate professional communication",
      "Present a polished professional image"
    ],
    icon: Briefcase,
    hasDetailPage: true
  },
  {
    id: "career-fair-strategies",
    slug: "career-fair-strategies",
    title: "Career Fair Strategies",
    category: "job-search",
    shortDescription: "Tips for navigating a career fair, including strategies on what to do before, during, and after a networking event with employers.",
    fullDescription: "Career fairs provide unique opportunities to meet multiple employers in one setting. Success at career fairs requires strategic preparation before, during, and after the event.",
    targetAudience: ["Students", "Job Seekers", "Career Changers"],
    outcomes: [
      "Research attending companies thoroughly",
      "Prepare and practice your elevator pitch",
      "Navigate booths strategically",
      "Follow up effectively within 24-48 hours"
    ],
    icon: Users,
    hasDetailPage: true
  },
  {
    id: "career-portfolio",
    slug: "career-portfolio",
    title: "Career Portfolio",
    category: "job-search",
    shortDescription: "Learn how to build an online portfolio to help you get organized, showcase your skills and accomplishments, and stand out from the crowd!",
    fullDescription: "A career portfolio is a curated collection of materials that showcase your skills, accomplishments, and professional growth.",
    targetAudience: ["Creative Professionals", "Students", "Job Seekers"],
    outcomes: [
      "Choose the right portfolio format",
      "Include essential documents and work samples",
      "Organize materials strategically",
      "Present your portfolio in interviews"
    ],
    icon: FolderOpen,
    hasDetailPage: true
  },
  {
    id: "dress-for-success",
    slug: "dress-for-success",
    title: "Dress for Success",
    category: "professional-skills",
    shortDescription: "Request this workshop for tips on making strong first impressions as it relates to interviewing and business situations.",
    fullDescription: "The way you dress communicates volumes before you speak a single word. Understanding dress codes and making appropriate choices is a crucial professional skill.",
    targetAudience: ["New Graduates", "Job Seekers", "Interns", "Professionals"],
    outcomes: [
      "Understand different dress code categories",
      "Build a professional wardrobe foundation",
      "Ensure proper fit and grooming",
      "Dress appropriately for virtual settings"
    ],
    icon: Shirt,
    hasDetailPage: true
  },
  {
    id: "graduate-school",
    slug: "graduate-school",
    title: "Graduate School: To Go or Not To Go",
    category: "education",
    shortDescription: "We will explore key factors in making the career decision to pursue an advanced degree through graduate or professional school.",
    fullDescription: "Pursuing a graduate or professional degree is a significant investment. This decision requires careful consideration of your career goals, financial situation, and industry requirements.",
    targetAudience: ["Undergraduate Students", "Early Career Professionals", "Career Changers"],
    outcomes: [
      "Assess if graduate school aligns with your goals",
      "Calculate the true cost and ROI",
      "Explore funding options",
      "Prepare strong applications"
    ],
    icon: GraduationCap,
    hasDetailPage: true
  },
  {
    id: "internship-search-strategies",
    slug: "internship-search-strategies",
    title: "Internship Search Strategies",
    category: "job-search",
    shortDescription: "Learn methods, explore tools, and gain resources related to finding, selecting, landing, and making the most out of internships.",
    fullDescription: "Internships provide invaluable real-world experience and often serve as a pathway to full-time employment.",
    targetAudience: ["Students", "Recent Graduates", "Career Changers"],
    outcomes: [
      "Start your search 3-6 months early",
      "Utilize multiple job search resources",
      "Tailor applications effectively",
      "Maximize the internship experience"
    ],
    icon: Search,
    hasDetailPage: true
  },
  {
    id: "interviewing",
    slug: "interviewing",
    title: "Interviewing Skills",
    category: "job-search",
    shortDescription: "Designed to help individuals learn how to prepare for the interview in terms of researching the employer, knowing what to expect, and how to combat anxiety.",
    fullDescription: "The job interview is your opportunity to bring your resume to life and demonstrate why you're the best candidate for the position.",
    targetAudience: ["Job Seekers", "Students", "Career Changers"],
    outcomes: [
      "Research companies and interviewers thoroughly",
      "Prepare STAR method stories",
      "Master non-verbal communication",
      "Handle different interview formats"
    ],
    icon: UserCheck,
    hasDetailPage: true
  },
  {
    id: "job-search-strategies-workshop",
    slug: "job-search-strategies-workshop",
    title: "Job Search Strategies",
    category: "job-search",
    shortDescription: "Provides an overview of key steps in the job search process, including job search myths, various job search methods, essential tools and strategies.",
    fullDescription: "An effective job search requires more than just applying to posted positions. Strategic job seekers use multiple methods to uncover opportunities.",
    targetAudience: ["Job Seekers", "Career Changers", "Workforce Programs"],
    outcomes: [
      "Define your target role and industry",
      "Utilize multiple search channels",
      "Network strategically",
      "Stay organized and maintain momentum"
    ],
    icon: Target,
    hasDetailPage: true
  },
  {
    id: "job-shadowing",
    slug: "job-shadowing",
    title: "Job Shadowing",
    category: "career-exploration",
    shortDescription: "Job shadowing allows students to try on a new work environment by observing a professional and gain information about potential career paths.",
    fullDescription: "Job shadowing provides a unique opportunity to observe professionals in their daily work environment, gaining realistic insights into potential career paths.",
    targetAudience: ["Students", "Career Changers", "Early Career Professionals"],
    outcomes: [
      "Identify job shadowing opportunities",
      "Prepare thoughtful questions",
      "Observe and engage actively",
      "Reflect on the experience effectively"
    ],
    icon: Eye,
    hasDetailPage: true
  },
  {
    id: "negotiating-job-offers",
    slug: "negotiating-job-offers",
    title: "Negotiating Job Offers",
    category: "career-advancement",
    shortDescription: "Individuals will learn about job evaluation and salary negotiation skills, including ethical considerations and dealing with multiple job offers!",
    fullDescription: "Salary and benefits negotiation is a critical skill that can impact your earnings significantly throughout your career.",
    targetAudience: ["Job Seekers", "Employees", "HR Professionals", "Managers"],
    outcomes: [
      "Research and know your market value",
      "Evaluate the full compensation package",
      "Make reasonable counter-offers",
      "Get everything in writing"
    ],
    icon: DollarSign,
    hasDetailPage: true
  },
  {
    id: "personal-branding",
    slug: "personal-branding",
    title: "Personal Branding",
    category: "professional-skills",
    shortDescription: "Effective professionals understand and can communicate their value and unique assets to others. This workshop provides an overview of the branding concept.",
    fullDescription: "Your personal brand is how you present yourself professionally to the world. A strong personal brand differentiates you in a competitive job market.",
    targetAudience: ["Professionals", "Job Seekers", "Entrepreneurs", "Students"],
    outcomes: [
      "Define your unique value proposition",
      "Craft your brand statement",
      "Align your online presence",
      "Create and share valuable content"
    ],
    icon: Award,
    hasDetailPage: true
  },
  {
    id: "professional-communication",
    slug: "professional-communication",
    title: "Professional Communication",
    category: "professional-skills",
    shortDescription: "Learn the importance of excellent oral and written communication skills while examining the do's and don'ts for emerging professionals.",
    fullDescription: "Effective communication is consistently ranked as one of the most important professional skills. Strong communicators are more likely to be promoted.",
    targetAudience: ["All Professionals", "New Graduates", "Managers"],
    outcomes: [
      "Adapt messages for different audiences",
      "Master email and presentation skills",
      "Navigate difficult conversations",
      "Seek and provide effective feedback"
    ],
    icon: MessageSquare,
    hasDetailPage: true
  },
  {
    id: "resume-critiquing",
    slug: "resume-critiquing",
    title: "Resume Critiquing",
    category: "job-search",
    shortDescription: "Designed for individuals who have developed resumes and would like assistance in making them better! Critique your own resume in a group format.",
    fullDescription: "A resume critique provides objective feedback on your resume's effectiveness, identifying areas for improvement in content, format, and impact.",
    targetAudience: ["Job Seekers", "Students", "Career Changers"],
    outcomes: [
      "Evaluate formatting and ATS compatibility",
      "Strengthen achievement statements",
      "Eliminate errors and inconsistencies",
      "Get multiple perspectives on your resume"
    ],
    icon: FileEdit,
    hasDetailPage: true
  },
  {
    id: "resume-writing",
    slug: "resume-writing",
    title: "Resume Writing & Cover Letters",
    category: "job-search",
    shortDescription: "Designed for individuals unsure of where to begin in writing a resume. We'll discuss the purpose, sections, styles, and formatting strategies.",
    fullDescription: "Your resume is often your first opportunity to make an impression on potential employers. A well-crafted resume clearly communicates your qualifications.",
    targetAudience: ["Job Seekers", "Students", "Career Changers", "Workforce Programs"],
    outcomes: [
      "Choose the right resume format",
      "Craft a strong summary section",
      "Showcase achievements with impact",
      "Write targeted cover letters"
    ],
    icon: PenTool,
    hasDetailPage: true
  },
  {
    id: "social-media-job-search",
    slug: "social-media-job-search",
    title: "Social Media and the Job Search",
    category: "job-search",
    shortDescription: "Optimize your job search by learning how to effectively use the most common social media channels utilized by recruiters.",
    fullDescription: "Social media has transformed how employers find candidates. Understanding how to leverage social platforms professionally is essential for modern job search success.",
    targetAudience: ["Job Seekers", "Students", "Professionals"],
    outcomes: [
      "Audit your online presence",
      "Optimize LinkedIn and Twitter profiles",
      "Research companies effectively",
      "Network authentically online"
    ],
    icon: Share2,
    hasDetailPage: true
  },
  {
    id: "writing-personal-statement",
    slug: "writing-personal-statement",
    title: "Writing a Personal Statement",
    category: "education",
    shortDescription: "An important workshop for any student considering graduate or professional school, applying for scholarships/fellowships.",
    fullDescription: "Personal statements are required for graduate school applications, scholarships, and many professional opportunities. A compelling personal statement tells your unique story.",
    targetAudience: ["Students", "Graduate Applicants", "Scholarship Applicants"],
    outcomes: [
      "Understand and address the prompt fully",
      "Tell your story compellingly",
      "Highlight your unique perspective",
      "Edit ruthlessly for impact"
    ],
    icon: Edit3,
    hasDetailPage: true
  }
];

// Calculate workshop price
export const calculateWorkshopPrice = (durationId, groupSizeId) => {
  const duration = workshopPricingMatrix.durations.find(d => d.id === durationId);
  const groupSize = workshopPricingMatrix.groupSizes.find(g => g.id === groupSizeId);
  
  if (!duration || !groupSize) return null;
  
  return Math.round(groupSize.basePrice * duration.multiplier);
};

// Service categories for filtering
export const serviceCategories = [
  { id: "all", label: "All Services" },
  { id: "coaching", label: "Individual Coaching" },
  { id: "workshops", label: "Workshops & Training" }
];

// Payment plan options
export const paymentOptions = {
  oneTime: {
    label: "One Payment",
    description: "Pay in full and get started immediately"
  },
  installment: {
    label: "Payment Plan",
    description: "$65 every 2 weeks for 4 payments",
    payments: 4,
    frequency: "bi-weekly",
    amount: 65
  }
};
